import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import {
  confirmPaymentMethods,
} from '../../services/billingService';
import {
  fetchOrganizationSubscription,
  clearOrganizationSubscriptionError,
} from '../../features/subscription/subscriptionSlice';
import { waitForPlanUpgrade } from '../../utils/billingPaymentFlow';
import {
  formatEnvelopeCreditsLabel,
  formatEnvelopeUsageSummary,
  formatUsageLine,
  getEnvelopeCreditsRemaining,
  shouldShowSubscriptionUsage,
} from '../../utils/subscription';
import { formatDateTimeUTC, formatDateUTC } from '../../utils/dateFormat';
import PaymentMethodsCard from './PaymentMethodsCard';

function formatPeriod(period) {
  if (!period) return '—';
  if (typeof period === 'string') {
    const formatted = formatDateTimeUTC(period);
    return formatted === 'N/A' ? '—' : formatted;
  }
  const start = period.start ?? period.starts_at ?? period.from;
  const end = period.end ?? period.ends_at ?? period.to;
  if (start && end) {
    return `${formatDateTimeUTC(start)} – ${formatDateTimeUTC(end)}`;
  }
  if (start) return formatDateTimeUTC(start);
  if (end) return formatDateTimeUTC(end);
  return '—';
}

const BillingPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSubscribedBanner, setShowSubscribedBanner] = useState(
    () => searchParams.get('subscribed') === '1'
  );
  const [syncingPlan, setSyncingPlan] = useState(false);
  const stripeReturnHandled = useRef(false);
  const userSubscription = useSelector((state) => state.subscription?.userSubscription);
  const {
    organizationSubscription,
    organizationStatus,
    organizationError,
  } = useSelector((state) => state.subscription);

  const subscription = organizationSubscription || userSubscription;

  useEffect(() => {
    dispatch(fetchOrganizationSubscription());
    return () => dispatch(clearOrganizationSubscriptionError());
  }, [dispatch]);

  useEffect(() => {
    const paymentIntentId = searchParams.get('payment_intent');
    const setupIntentId = searchParams.get('setup_intent');
    const expectedPlan = searchParams.get('expected_plan');
    const subscribed = searchParams.get('subscribed') === '1';

    if (stripeReturnHandled.current) return undefined;

    const syncAfterStripeReturn = async () => {
      stripeReturnHandled.current = true;
      setSyncingPlan(true);
      try {
        if (paymentIntentId || setupIntentId) {
          await confirmPaymentMethods({
            paymentIntentId: paymentIntentId || undefined,
            setupIntentId: setupIntentId || undefined,
            setAsDefault: true,
          });
          if (expectedPlan) {
            await waitForPlanUpgrade(expectedPlan, {
              fetchSubscription: () => dispatch(fetchOrganizationSubscription()).unwrap(),
            });
          } else {
            await dispatch(fetchOrganizationSubscription());
          }
        } else {
          await dispatch(fetchOrganizationSubscription());
        }
        setShowSubscribedBanner(true);
      } catch {
        setShowSubscribedBanner(subscribed);
      } finally {
        setSyncingPlan(false);
        const next = new URLSearchParams(searchParams);
        [
          'subscribed',
          'plan',
          'expected_plan',
          'payment_intent',
          'payment_intent_client_secret',
          'setup_intent',
          'setup_intent_client_secret',
          'redirect_status',
          'session_id',
        ].forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      }
    };

    if (paymentIntentId || setupIntentId) {
      syncAfterStripeReturn();
      return undefined;
    }

    if (subscribed) {
      const next = new URLSearchParams(searchParams);
      next.delete('subscribed');
      next.delete('plan');
      next.delete('expected_plan');
      setSearchParams(next, { replace: true });
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showUsage = shouldShowSubscriptionUsage(subscription);
  const envelopeUsageLine = showUsage
    ? formatEnvelopeUsageSummary(subscription, formatDateUTC)
    : null;
  const usersUsageLine = showUsage
    ? formatUsageLine(subscription.usage, 'users')
    : null;
  const prepaidCredits = getEnvelopeCreditsRemaining(subscription);
  const prepaidCreditsLabel = formatEnvelopeCreditsLabel(prepaidCredits);
  const nonMeteredMessage =
    !showUsage && subscription
      ? (subscription.message
        || (subscription.bypass
          ? 'Quota does not apply to this account.'
          : 'Commercial subscription metering does not apply to this organization.'))
      : null;

  return (
    <div className="billing-page">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <h4 className="fw-semibold mb-1">Billing &amp; subscription</h4>
          <p className="text-muted mb-0">Manage your organization plan and usage.</p>
        </div>
        <Button as={Link} to="/plans" variant="primary" size="sm">
          Change plan
        </Button>
      </div>

      {((organizationStatus === 'loading' && !subscription) || syncingPlan) && (
        <div className="d-flex justify-content-center align-items-center py-5 gap-2">
          <Spinner animation="border" variant="primary" />
          {syncingPlan && (
            <p className="text-muted small mb-0">Confirming your subscription…</p>
          )}
        </div>
      )}

      {showSubscribedBanner && (
        <Alert variant="success" className="mb-4">
          Your subscription was updated successfully.
        </Alert>
      )}

      {organizationError && (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{organizationError}</span>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => dispatch(fetchOrganizationSubscription())}
          >
            Retry
          </Button>
        </Alert>
      )}

      {subscription && (
        <Card className="shadow-sm">
          <Card.Body className="p-4">
            <div className="row g-4">
              <div className="col-md-6">
                <div className="text-muted small text-uppercase mb-1">Plan</div>
                <div className="fs-5 fw-semibold">
                  {subscription.planName || '—'}
                  {subscription.planCode && (
                    <span className="text-muted small ms-2">({subscription.planCode})</span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small text-uppercase mb-1">Status</div>
                <div className="fs-5 fw-semibold text-capitalize">
                  {(subscription.subscriptionStatus || '—').replace(/_/g, ' ')}
                </div>
              </div>
              {showUsage && (
                <div className="col-md-6">
                  <div className="text-muted small text-uppercase mb-1">Current period</div>
                  <div>{formatPeriod(subscription.currentPeriod)}</div>
                </div>
              )}
              {showUsage ? (
                <>
                  <div className="col-md-6">
                    <div className="text-muted small text-uppercase mb-1">Envelope usage</div>
                    <div>{envelopeUsageLine || '—'}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small text-uppercase mb-1">Users</div>
                    <div>{usersUsageLine || '—'}</div>
                  </div>
                </>
              ) : (
                <div className="col-12">
                  <div className="text-muted small text-uppercase mb-1">Usage</div>
                  <div>{nonMeteredMessage}</div>
                </div>
              )}
              <div className="col-md-6">
                <div className="text-muted small text-uppercase mb-1">Prepaid credits</div>
                <div>
                  {prepaidCreditsLabel
                    || (prepaidCredits === 0 ? '0 prepaid credits' : '—')}
                  {prepaidCredits != null && prepaidCredits > 0 && (
                    <span className="text-muted small ms-1">(never expire)</span>
                  )}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {!subscription && organizationStatus === 'succeeded' && (
        <Alert variant="info">No subscription details are available for this organization.</Alert>
      )}

      {/* Stripe payment methods UI remains for Phase 6; endpoints may be unavailable until then. */}
      <PaymentMethodsCard />
    </div>
  );
};

export default BillingPage;
